import { type FC, useMemo } from "react";

import { ReplayDiffBanner, ReplayDiffPrefixRows } from "./contextHeader";
import {
  type MessageEntity,
  type StepDetail,
  fmtTime,
  fmtTokens,
  getReplayContextSummary,
  shortSha,
} from "./observabilityModel";
import { Overlay } from "./Overlays";

export const DiffOverlay: FC<{
  a: StepDetail;
  b: StepDetail;
  onClose: () => void;
}> = ({ a, b, onClose }) => {
  const rows = useMemo(() => diffRows(a.messages, b.messages), [a, b]);
  const aReplay = getReplayContextSummary(a.step);
  const bReplay = getReplayContextSummary(b.step);
  return (
    <Overlay
      onClose={onClose}
      title={`Diff — #${a.step.stepIndexInWake} → #${b.step.stepIndexInWake}`}
    >
      <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-2 sm:divide-x sm:divide-y-0 dark:divide-slate-800">
        <div className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">
          a · {fmtTime(a.step.startedAt)} · {fmtTokens(a.step.inputTokens)}
        </div>
        <div className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">
          b · {fmtTime(b.step.startedAt)} · {fmtTokens(b.step.inputTokens)}
        </div>
      </div>
      <ReplayDiffBanner a={aReplay} b={bReplay} />
      <ReplayDiffPrefixRows a={aReplay} b={bReplay} />
      <div className="border-t border-slate-100 dark:border-slate-800">
        {rows.map((row, i) => (
          <DiffRow key={i} row={row} />
        ))}
      </div>
    </Overlay>
  );
};

type DiffRowT =
  | { kind: "same"; a: MessageEntity; b: MessageEntity }
  | { kind: "changed"; a: MessageEntity; b: MessageEntity }
  | { kind: "added"; b: MessageEntity }
  | { kind: "removed"; a: MessageEntity };

const DIFF_BACKGROUND_CLASS_BY_KIND: Record<DiffRowT["kind"], string> = {
  added: "bg-emerald-50 dark:bg-emerald-900/20",
  changed: "bg-amber-50 dark:bg-amber-900/20",
  removed: "bg-rose-50 dark:bg-rose-900/20",
  same: "",
};

const DIFF_SIGN_BY_KIND: Record<DiffRowT["kind"], string> = {
  added: "+",
  changed: "~",
  removed: "−",
  same: " ",
};

const DIFF_SIGN_COLOR_BY_KIND: Record<DiffRowT["kind"], string> = {
  added: "text-emerald-600 dark:text-emerald-400",
  changed: "text-amber-600 dark:text-amber-400",
  removed: "text-rose-600 dark:text-rose-400",
  same: "text-slate-500 dark:text-slate-300",
};

const getDiffBackgroundClass = (kind: DiffRowT["kind"]) =>
  DIFF_BACKGROUND_CLASS_BY_KIND[kind];

const getDiffSign = (kind: DiffRowT["kind"]) => DIFF_SIGN_BY_KIND[kind];

const getDiffSignColor = (kind: DiffRowT["kind"]) =>
  DIFF_SIGN_COLOR_BY_KIND[kind];

function diffRowKey(m: MessageEntity): string {
  return `${m.sourceKind}:${m.sequence}`;
}

function diffRows(a: MessageEntity[], b: MessageEntity[]): DiffRowT[] {
  const aMap = new Map(a.map((m) => [diffRowKey(m), m] as const));
  const bMap = new Map(b.map((m) => [diffRowKey(m), m] as const));
  const allKeys = new Set([...aMap.keys(), ...bMap.keys()]);
  const sortable = [...allKeys].map((k) => {
    const am = aMap.get(k);
    const bm = bMap.get(k);
    const seq = (bm ?? am)!.sequence;
    return { a: am, b: bm, key: k, seq };
  });
  sortable.sort((x, y) => x.seq - y.seq);
  return sortable.map((entry): DiffRowT => {
    if (entry.a && entry.b) {
      return entry.a.textSha === entry.b.textSha
        ? { a: entry.a, b: entry.b, kind: "same" }
        : { a: entry.a, b: entry.b, kind: "changed" };
    }
    if (entry.b) {
      return { b: entry.b, kind: "added" };
    }
    return { a: entry.a!, kind: "removed" };
  });
}

const DiffRow: FC<{ row: DiffRowT }> = ({ row }) => {
  const bg = getDiffBackgroundClass(row.kind);
  const sign = getDiffSign(row.kind);
  const signColor = getDiffSignColor(row.kind);
  return (
    <div
      className={`grid grid-cols-1 divide-y divide-slate-100 font-mono text-[10px] sm:grid-cols-2 sm:divide-x sm:divide-y-0 dark:divide-slate-800 ${bg}`}
    >
      <div className="flex min-w-0 items-center gap-2 px-3 py-1">
        <span className={`w-3 shrink-0 ${signColor}`}>
          {row.kind === "added" ? "" : sign}
        </span>
        {/* Side label only visible on narrow — the column header already
            names A/B on desktop, so this only appears when columns stack. */}
        <span className="w-3 shrink-0 text-slate-500 sm:hidden dark:text-slate-300">
          A
        </span>
        {"a" in row && row.a ? (
          <MessageOneLiner msg={row.a} />
        ) : (
          <span className="min-w-0 flex-1 truncate text-slate-500 sm:hidden dark:text-slate-300">
            —
          </span>
        )}
      </div>
      <div className="flex min-w-0 items-center gap-2 px-3 py-1">
        <span className={`w-3 shrink-0 ${signColor}`}>
          {row.kind === "removed" ? "" : sign}
        </span>
        <span className="w-3 shrink-0 text-slate-500 sm:hidden dark:text-slate-300">
          B
        </span>
        {"b" in row && row.b ? (
          <MessageOneLiner msg={row.b} />
        ) : (
          <span className="min-w-0 flex-1 truncate text-slate-500 sm:hidden dark:text-slate-300">
            —
          </span>
        )}
      </div>
    </div>
  );
};

const MessageOneLiner: FC<{ msg: MessageEntity }> = ({ msg }) => (
  <>
    <span className="w-5 shrink-0 text-slate-400">{msg.sequence}</span>
    {/* Role hidden on narrow — source_kind already tells you role semantically.
        `w-12` wasn't wide enough for "ASSISTANT" at 10px monospace, causing
        the label to bleed into the next column on mobile 2-col diff view. */}
    <span className="hidden w-20 shrink-0 uppercase tracking-wider text-slate-500 sm:inline">
      {msg.role}
    </span>
    <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-300">
      {msg.sourceKind}
    </span>
    {/* Sha + cacheControl hidden on narrow — shown only when room allows. */}
    <span className="hidden min-w-0 shrink truncate text-slate-400 sm:inline">
      {shortSha(msg.textSha, 10)}
      {msg.cacheControl ? ` · ${msg.cacheControl}` : ""}
    </span>
  </>
);

// ---------------------------------------------------------------------------
// Overlay — lightweight inline modal. Avoids Portal deps; slides in from the
// Right edge of the drawer, staying within the SideSheet bounds.
// ---------------------------------------------------------------------------
